import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { initDataDog } from './services/datadog.ts';
import { initializeFirebase } from './config/firebase';
import { analytics } from './services/analytics';

analytics.initialize();

// Initialize DataDog
initDataDog();

// Log environment variables (only in local, development, or staging)
const logEnvironmentVariables = () => {
  const env = import.meta.env.VITE_ENV;
  const allowedEnvs = ['local', 'development', 'staging'];

  if (env && allowedEnvs.includes(env)) {
    console.log('=== Environment Variables (Obfuscated) ===');

    // Function to obfuscate values
    const obfuscate = (value: string | undefined): string => {
      if (!value) return 'undefined';
      const length = value.length;
      if (length <= 8) {
        return value.substring(0, 2) + '...' + value.substring(length - 2);
      }
      return value.substring(0, 4) + '...' + value.substring(length - 4);
    };

    // Log all known environment variables
    const envVars = {
      VITE_ENV: import.meta.env.VITE_ENV,
      VITE_GRAPHQL_ENDPOINT: import.meta.env.VITE_GRAPHQL_ENDPOINT,
      VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
      VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
      VITE_APP_DESCRIPTION: import.meta.env.VITE_APP_DESCRIPTION,
      VITE_GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      VITE_APP_LOKALISE_API_KEY: import.meta.env.VITE_APP_LOKALISE_API_KEY,
      VITE_APP_LOKALISE_PROJECT_ID: import.meta.env.VITE_APP_LOKALISE_PROJECT_ID,
      VITE_DATADOG_CLIENT_TOKEN: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
      VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
      VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
      VITE_FIREBASE_VAPID_KEY: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      VITE_FIREBASE_MEASUREMENT_ID: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    };

    Object.entries(envVars).forEach(([key, value]) => {
      if (
        key === 'VITE_ENV' ||
        key === 'VITE_APP_NAME' ||
        key === 'VITE_APP_VERSION' ||
        key === 'VITE_APP_DESCRIPTION'
      ) {
        // Don't obfuscate these metadata fields
        console.log(key + ': ' + value);
      } else {
        console.log(key + ': ' + obfuscate(value));
      }
    });

    console.log('==========================================');
  }
};

// Log environment variables
logEnvironmentVariables();

// Register Service Worker for Firebase Cloud Messaging
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/firebase-messaging-sw.js')
    .then(registration => {
      console.log('Service Worker registered:', registration);
    })
    .catch(error => {
      console.error('Service Worker registration failed:', error);
    });
}

// Initialize Firebase (async)
initializeFirebase().catch(error => {
  console.error('Failed to initialize Firebase:', error);
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
